import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Observable, of, Subscription, timeout } from 'rxjs';

import { PollingResult, SubmissionStatusService } from '@features/opportunities/services/submission-status.service';
import { MonitoringService } from '@core/services/monitoring.service';
import { MonitoringEvents } from '@core/services/monitoring-events.constants';
import { TextAttributes } from '@shared/enums/customisation.enum';
import {
  OpportunityType,
  OpportunityWorkflowDisplayState,
  OpportunityWorkflowStatus
} from '@shared/models/opportunity';
import { CustomisationService } from '@shared/services/customisation/customisation.service';

import { CustomerEffortScoreComponent } from './customerEffortScore.component';

@Component({
  selector: 'app-submission-status-modal',
  templateUrl: './submission-status-modal.component.html',
  styleUrls: ['./submission-status-modal.component.scss']
})

export class SubmissionStatusModalComponent implements OnInit, OnDestroy {
  @Input() opportunityId!: string;
  @Input() opportunityType!: OpportunityType;
  @Output() redirect = new EventEmitter<void>();
  @ViewChild(CustomerEffortScoreComponent) CustomerEffortScore?: CustomerEffortScoreComponent;
  private subscription?: Subscription;

  constructor(
    private submissionStatusService: SubmissionStatusService,
    private customisationService: CustomisationService,
    public bsModalRef: BsModalRef,
    private monitoringService: MonitoringService
  ) {}

  isComplete = false;
  timedOut = false;
  workflowDisplayState?: OpportunityWorkflowDisplayState;

  submissionModalHeaderSubmittingText = this.customisationService.getText(
    TextAttributes.SubmissionModalHeaderSubmitting
  );
  submissionModalHeaderSubmittedText = this.customisationService.getText(
    TextAttributes.SubmissionModalHeaderSubmitted
  );
  submissionInProgressText = this.customisationService.getText(
    TextAttributes.SubmissionInProgress
  );
  submissionCompleteText = this.customisationService.getText(
    TextAttributes.SubmissionComplete
  );
  submissionCompleteButtonText = this.customisationService.getText(
    TextAttributes.SubmissionModalButton
  );
  submissionStatusTimedOutText = this.customisationService.getText(
    TextAttributes.SubmissionStatusTimeOut
  );

  ngOnInit() {
    this.subscription = this.startPolling().subscribe({
      next: (result) => {
        this.isComplete = result.submissionComplete;
        this.timedOut = result.timedOut;
        
        // If submission failed, hide this polling modal and let the existing error modal show
        if (result.workflowDisplayState?.status === OpportunityWorkflowStatus.SubmissionFailed) {
          // Track submission failure
          this.monitoringService.logEvent(MonitoringEvents.SUBMISSION_FAILED, {
            opportunityId: this.opportunityId,
            opportunityType: this.opportunityType
          });
          
          this.bsModalRef.hide();
          return;
        }
        if (result.submissionComplete) {
          this.workflowDisplayState = result.workflowDisplayState;
        }
      },
      error: (error) => {
        this.monitoringService.logException(
          error instanceof Error ? error : new Error('Modal polling error'),
          3, // SeverityLevel.Error
          { opportunityId: this.opportunityId, opportunityType: this.opportunityType }
        );
        // Update UI to show error state
        this.timedOut = true;
      },
      complete: () => {}
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  startPolling(): Observable<PollingResult> {
    return this.submissionStatusService.startPolling(this.opportunityId);
  }

  onRedirect() {
    if (this.workflowDisplayState) {
      this.submissionStatusService.handleSubmissionComplete(
        this.opportunityId,
        this.workflowDisplayState
      );
    }
    this.redirect.emit();
    this.bsModalRef.hide();
  }
}
